import { useCallback, useEffect, useRef, useState } from "react"
import {
  ContentItemCreationStatus,
  type ContentItem,
} from "@/shared/api/generated"
import {
  usePdfDocument,
  usePdfLayout,
  useVirtualScroll,
  usePageRenderer,
  usePageMeta,
  useAudioPage,
  useActiveWordTracker,
  useThumbnailRenderer,
} from "@/entities/pdf-viewer"
import { activePlayer } from "@/entities/audio-engine"
import { processSocket } from "@/entities/process-socket/store"
import {
  DISCOVERED_TRANSLATION_KEYS,
  LANG_KEYS,
  SOCKET_RESPONSES,
  ROUTER_PATHS,
} from "@/shared/constants"
import { useAutoTranslation } from "@/entities"
import { Spinner } from "@/components/ui/spinner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { ChevronLeft, ChevronRight, Undo2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { PDF_VIEWER_BUFFER_PAGES } from "@/entities/pdf-viewer/config"

interface PdfViewerProps {
  itemData: ContentItem
}

export const PdfViewer = ({ itemData }: PdfViewerProps) => {
  const { t } = useAutoTranslation()
  const isProcessing =
    itemData.creationStatus ===
    ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING

  const navigate = useNavigate()

  const { pdfDoc, totalPages, loading, originalPageSizes } = usePdfDocument(
    itemData.pdfUploaded
  )

  const { pageInfos, totalHeight, baseScale, scrollContainerRef } =
    usePdfLayout(totalPages, originalPageSizes)

  const { visibleRange, currentPage } = useVirtualScroll(
    scrollContainerRef,
    pageInfos,
    itemData.id
  )

  const { renderedPages, canvasRefs } = usePageRenderer(
    pdfDoc,
    pageInfos,
    visibleRange,
    baseScale
  )

  const { thumbnailCanvases } = useThumbnailRenderer(pdfDoc, totalPages)

  const combinedUrls = isProcessing
    ? undefined
    : itemData.combinedDataAbsoluteUrls

  const { audioPage, setAudioPage, searchForPosition } = useAudioPage(
    itemData.id,
    totalPages,
    combinedUrls
  )

  const { getPageWords, invalidate: invalidatePageMeta } = usePageMeta(
    itemData.id,
    audioPage,
    currentPage,
    combinedUrls
  )

  // Audio-first chunks announce their words separately (CHUNK_WORDS), so
  // refetch page words on both events.
  useEffect(() => {
    if (!isProcessing) return
    const handler = () => invalidatePageMeta()
    processSocket.on(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
    processSocket.on(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    return () => {
      processSocket.off(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
      processSocket.off(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    }
  }, [isProcessing, invalidatePageMeta])

  const wordElementRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  const { activeWord, autoScroll, setAutoScroll } = useActiveWordTracker(
    getPageWords,
    pageInfos,
    audioPage,
    scrollContainerRef,
    wordElementRefs,
    setAudioPage,
    combinedUrls ? searchForPosition : undefined
  )

  // On mount: play if user navigated within app, or just load (no play) on browser reload.
  const hasStartedAudio = useRef(false)
  useEffect(() => {
    if (hasStartedAudio.current) return
    hasStartedAudio.current = true
    if (activePlayer.id === itemData.id && activePlayer.isActive) return
    if (navigator.userActivation?.hasBeenActive) {
      activePlayer.play(itemData)
    } else {
      activePlayer.load(itemData)
    }
  }, [itemData])

  const handleWordClick = (word: { start_ts: number }) => {
    activePlayer.seekToAndPlay(word.start_ts)
  }

  // ─── Safety net: force scroll to audioPage if we drifted ──

  useEffect(() => {
    if (!autoScroll || !pageInfos.length) return

    const enforce = () => {
      if (!activePlayer.isPlaying || audioPage <= 0) return
      if (!scrollContainerRef.current) return

      // Check if current scroll position is anywhere near the audio page
      const container = scrollContainerRef.current
      const targetPage = pageInfos.find(p => p.pageNumber === audioPage)
      if (!targetPage) return

      const scrollTop = container.scrollTop
      const viewportHeight = container.clientHeight
      const pageTop = targetPage.yPosition
      const pageBottom = pageTop + targetPage.height

      // Is the audio page visible at all?
      const pageVisible =
        pageBottom > scrollTop && pageTop < scrollTop + viewportHeight

      if (!pageVisible) {
        // We're on the wrong page — instant jump
        container.scrollTo({
          top: pageTop,
          behavior: "instant",
        })
      }
    }

    const id = setInterval(enforce, 500)
    return () => clearInterval(id)
  }, [autoScroll, audioPage, pageInfos, scrollContainerRef])

  // ─── Page navigation ────────────────────────────────────

  const [pageInputValue, setPageInputValue] = useState("1")

  useEffect(() => {
    setPageInputValue(String(currentPage))
  }, [currentPage])

  const navigateToPage = useCallback(
    (pageNumber: number) => {
      if (pageNumber < 1 || pageNumber > totalPages) return
      const pageInfo = pageInfos.find(p => p.pageNumber === pageNumber)
      if (!pageInfo || !scrollContainerRef.current) return
      setAutoScroll(false)
      scrollContainerRef.current.scrollTo({
        top: pageInfo.yPosition,
        behavior: "smooth",
      })
    },
    [pageInfos, totalPages, scrollContainerRef, setAutoScroll]
  )

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseInt(pageInputValue, 10)
    if (!isNaN(num)) navigateToPage(num)
  }

  // ─── Sidebar auto-scroll to active thumbnail ────────────

  const sidebarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sidebarRef.current || totalPages === 0) return
    const el = sidebarRef.current.querySelector(
      `[data-page-number="${currentPage}"]`
    )
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [currentPage, totalPages])

  return (
    <div className="flex h-full w-full flex-col">
      {/* Top Navigation Bar */}
      <div
        className="bg-background flex items-center justify-between border-b px-2"
        style={{ minHeight: "56px" }}
      >
        {/* Left: Back button */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(ROUTER_PATHS.APP_LIBRARY)}
          >
            <Undo2 className="h-5 w-5" />
          </Button>
        </div>

        {/* Center: Page Navigation */}
        {!loading && totalPages > 0 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <form
              onSubmit={handlePageInputSubmit}
              className="flex items-center gap-1"
            >
              <Input
                type="number"
                min={1}
                max={totalPages}
                value={pageInputValue}
                onChange={e => setPageInputValue(e.target.value)}
                onBlur={handlePageInputSubmit}
                className="h-8 w-14 text-center text-sm"
              />
              <span className="text-muted-foreground text-sm">
                {t(DISCOVERED_TRANSLATION_KEYS.OF)} {totalPages}
              </span>
            </form>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Right: Auto Scroll */}
        {!loading && (
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="auto-scroll"
              checked={autoScroll}
              onCheckedChange={checked => setAutoScroll(checked === true)}
            />
            <Label
              htmlFor="auto-scroll"
              className="cursor-pointer text-sm font-medium"
            >
              {t(LANG_KEYS.AUTO_SCROLL)}
            </Label>
          </div>
        )}
      </div>

      {/* Main content: pdf + sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* PDF Scroll Container */}
        <div
          className="flex-1 overflow-auto bg-gray-100"
          ref={scrollContainerRef}
          style={{ padding: "20px" }}
        >
          {loading && (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm font-medium">
                  {t(LANG_KEYS.LOADING)}
                </span>
                <Spinner className="text-muted-foreground size-4" />
              </div>
            </div>
          )}

          {!loading && pageInfos.length > 0 && (
            <div
              style={{
                position: "relative",
                width: "100%",
                height: `${totalHeight}px`,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {pageInfos.map((pageInfo, index) => {
                const shouldRender = (() => {
                  if (visibleRange.start === 0 && visibleRange.end === 0) {
                    return index <= PDF_VIEWER_BUFFER_PAGES * 2
                  }
                  return (
                    index >= visibleRange.start && index <= visibleRange.end
                  )
                })()

                if (!shouldRender) {
                  return (
                    <div
                      key={`page-${pageInfo.pageNumber}`}
                      style={{
                        position: "absolute",
                        top: `${pageInfo.yPosition}px`,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "auto",
                        height: `${pageInfo.height}px`,
                      }}
                    />
                  )
                }

                const canvas = canvasRefs.current.get(pageInfo.pageNumber)
                const isRendered = renderedPages.has(pageInfo.pageNumber)
                const words = getPageWords(pageInfo.pageNumber)
                const canvasCssWidth = canvas
                  ? Number.parseFloat(canvas.style.width) || 0
                  : 0
                const canvasCssHeight = canvas
                  ? Number.parseFloat(canvas.style.height) || 0
                  : 0

                return (
                  <div
                    key={`page-${pageInfo.pageNumber}`}
                    style={{
                      position: "absolute",
                      top: `${pageInfo.yPosition}px`,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "auto",
                      height: `${pageInfo.height}px`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isRendered && canvas ? (
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                        ref={el => {
                          if (el && canvas && !el.contains(canvas)) {
                            el.appendChild(canvas)
                          }
                        }}
                      >
                        {words.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: `${canvasCssWidth}px`,
                              height: `${canvasCssHeight}px`,
                            }}
                          >
                            {words.map((word, wordIndex) => {
                              const scaledX = word.x * baseScale
                              const scaledWidth = word.width * baseScale
                              const scaledHeight = word.height * baseScale
                              const scaledY =
                                (pageInfo.originalViewportHeight -
                                  word.y -
                                  word.height) *
                                baseScale

                              const isActive =
                                activeWord?.pageNumber ===
                                  pageInfo.pageNumber &&
                                activeWord?.wordIndex === wordIndex

                              const wordKey = `w-${pageInfo.pageNumber}-${wordIndex}`

                              return (
                                <div
                                  key={wordKey}
                                  ref={el => {
                                    if (el) {
                                      wordElementRefs.current.set(wordKey, el)
                                    }
                                  }}
                                  onClick={() => handleWordClick(word)}
                                  data-word={word.word}
                                  style={{
                                    position: "absolute",
                                    left: `${scaledX}px`,
                                    top: `${scaledY}px`,
                                    width: `${scaledWidth}px`,
                                    height: `${scaledHeight}px`,
                                    border: "1px solid var(--primary-border)",
                                    backgroundColor: isActive
                                      ? "var(--primary-highlight)"
                                      : "var(--primary-subtle)",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    if (!isActive) {
                                      e.currentTarget.style.backgroundColor =
                                        "var(--primary-border)"
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (!isActive) {
                                      e.currentTarget.style.backgroundColor =
                                        "var(--primary-subtle)"
                                    }
                                  }}
                                />
                              )
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Spinner className="text-muted-foreground size-4" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right Sidebar - Page Thumbnails */}
        {!loading && totalPages > 0 && (
          <div
            className="bg-background hidden border-l xl:block"
            style={{ minWidth: "192px", maxWidth: "192px" }}
          >
            <div className="border-b px-4 py-2">
              <h3 className="text-sm font-semibold">
                {t(DISCOVERED_TRANSLATION_KEYS.PAGES)}
              </h3>
            </div>
            <div
              ref={sidebarRef}
              className="overflow-y-auto"
              style={{ height: "calc(100% - 41px)" }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                pageNumber => {
                  const thumbnailCanvas =
                    thumbnailCanvases.current.get(pageNumber)
                  const isActive = currentPage === pageNumber

                  return (
                    <div
                      key={`thumb-${pageNumber}`}
                      data-page-number={pageNumber}
                      onClick={() => navigateToPage(pageNumber)}
                      className="cursor-pointer p-2 transition-all"
                      style={{
                        border: isActive
                          ? "2px solid var(--primary)"
                          : "2px solid transparent",
                        backgroundColor: isActive
                          ? "var(--primary-subtle)"
                          : "transparent",
                      }}
                      onMouseEnter={e => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor =
                            "rgba(0,0,0,0.05)"
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "transparent"
                        }
                      }}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {thumbnailCanvas ? (
                          <div
                            className="flex w-full justify-center"
                            ref={el => {
                              if (
                                el &&
                                thumbnailCanvas &&
                                !el.contains(thumbnailCanvas)
                              ) {
                                el.appendChild(thumbnailCanvas)
                              }
                            }}
                          />
                        ) : (
                          <div
                            className="flex w-full items-center justify-center bg-gray-100 text-xs text-gray-500"
                            style={{ height: "120px" }}
                          >
                            {t(LANG_KEYS.LOADING)}
                          </div>
                        )}
                        <span
                          className="text-xs"
                          style={{
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? "var(--primary)" : "inherit",
                          }}
                        >
                          {pageNumber}
                        </span>
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
